import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../utils/trpc';
import { supabase } from '../utils/supabase';
import styles from '../styles/VenueProfile.module.css';
import { buildVenueMapQuery } from '../utils/location';
import VenueMapEmbed from '../components/VenueMapEmbed';
import { EventCard } from '../components/EventCard';

interface VenueProfileProps {
  venueId?: string;
  onClose?: () => void;
}

function VenueProfile({ venueId, onClose }: VenueProfileProps) {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [reported, setReported] = useState(false);
  const [showTags, setShowTags] = useState(false);

  const {
    data: venue,
    isLoading: venueLoading,
    error: venueError,
  } = trpc.venue.getById.useQuery({ venue_id: venueId || '' }, { enabled: !!venueId });

  const { data: venueTags, isLoading: tagsLoading } = trpc.tags.getVenueTags.useQuery(
    { venueId: venue?.venue_id || '' },
    { enabled: !!venue?.venue_id },
  );

  const { data: galleryImages, isLoading: galleryLoading } =
    trpc.venue.getImages.useQuery(
      { venue_id: venue?.venue_id || '' },
      { enabled: !!venue?.venue_id },
    );

  const { data: upcomingEvents, isLoading: eventsLoading } =
    trpc.event.getEventsByVenueId.useQuery(
      {
        venueId: venue?.venue_id || '',
        upcomingOnly: true,
      },
      {
        enabled: !!venue?.venue_id,
      },
    );

  const handleClose = () => {
    venueError || !venue ? navigate(-1) : onClose ? onClose() : '';
  };

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleReport = () => {
    setReported(true);
    setShowMenu(false);
  };

  const formatTime = (time: string | null) => {
    if (!time) return null;
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const isLoading = venueLoading || tagsLoading || galleryLoading || eventsLoading;

  if (isLoading) {
    return (
      <div className={styles.backdrop} onClick={handleBackdrop}>
        <div className={styles.profileCard} onClick={(e) => e.stopPropagation()}>
          <div className={styles.heroBanner} />
          <button
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Close profile"
          >
            ✕
          </button>
          <div className={styles.cardInner}>
            <div className={styles.skeletonAvatar} />
            <div className={styles.skeletonTitle} />
            <div className={styles.skeletonText} />
            <div className={styles.skeletonText} />
            <div className={styles.skeletonText} />
            <div className={styles.skeletonTags}>
              <div className={styles.skeletonTag} />
              <div className={styles.skeletonTag} />
              <div className={styles.skeletonTag} />
              <div className={styles.skeletonTag} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (venueError || !venue) {
    return (
      <div className={styles.backdrop} onClick={handleBackdrop}>
        <div className={styles.profileCard} onClick={(e) => e.stopPropagation()}>
          <div className={styles.heroBanner} />
          <button
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Close profile"
          >
            ✕
          </button>
          <div className={styles.cardInner}>
            <div className={styles.errorContent}>
              <div className={styles.errorIcon}>❌</div>
              <h2 className={styles.errorTitle}>Venue Not Found</h2>
              <p className={styles.errorMessage}>
                {venueError
                  ? venueError.message
                  : `"${venueId ?? 'This venue'}" doesn't exist.`}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const openTime = formatTime(venue.open_time);
  const closeTime = formatTime(venue.close_time);
  const tags = venueTags?.map((vt: any) => vt.tags.name) || [];
  const mapQuery = buildVenueMapQuery(venue.location, venue.address);

  return (
    <div className={styles.backdrop} onClick={handleBackdrop}>
      <div className={styles.profileCard} onClick={(e) => e.stopPropagation()}>
        {venue.banner_url ? (
          <div className={styles.bannerContainer}>
            <img
              onLoad={(e) => e.currentTarget.classList.add('loaded')}
              src={venue.banner_url}
              alt="Venue Banner"
              className={styles.bannerImage}
            />
          </div>
        ) : (
          <div className={styles.heroBanner} />
        )}

        <div className={styles.controls}>
          <div className={styles.menuContainer}>
            <button
              className={styles.menuButton}
              onClick={() => setShowMenu(!showMenu)}
              aria-label="Options menu"
            >
              ⋮
            </button>
            {showMenu && (
              <div className={styles.dropdown}>
                <button
                  className={styles.dropdownItem}
                  onClick={handleReport}
                  disabled={reported}
                >
                  {reported ? '✓ Reported' : 'Report Venue'}
                </button>
              </div>
            )}
          </div>
          <button
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Close profile"
          >
            ✕
          </button>
        </div>

        <div className={styles.cardInner}>
          <h1 className={styles.username}>{venue.venue_name}</h1>

          <div className={styles.infoGrid}>
            {venue.desc && (
              <div className={styles.infoField}>
                <span className={styles.label}>Description</span>
                <span className={styles.value}>{venue.desc}</span>
              </div>
            )}

            {openTime && closeTime && (
              <div className={styles.infoField}>
                <span className={styles.label}>Hours</span>
                <span className={styles.value}>
                  {openTime} - {closeTime}
                </span>
              </div>
            )}

            {(venue.address || venue.max_capacity) && (
              <div className={styles.infoRow}>
                {venue.address && (
                  <div className={styles.infoField}>
                    <span className={styles.label}>Location</span>
                    <span className={styles.value}>{venue.address}</span>
                  </div>
                )}
                {venue.max_capacity && (
                  <div className={styles.infoField}>
                    <span className={styles.label}>Capacity</span>
                    <span className={styles.value}>{venue.max_capacity}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <h2 className={styles.galleryTitle}>Events</h2>
            {upcomingEvents?.length === 0 ? (
              <p>No upcoming events.</p>
            ) : (
              upcomingEvents?.map((event: any) => (
                <EventCard
                  key={event.event_id}
                  event={{
                    event_id: event.event_id,
                    event_name: event.event_name,
                    description: event.description,
                    date: event.date,
                    duration_minutes: event.duration_minutes,
                    comments: event.comments ?? 0,
                    rsvpCount: event.rsvpCount ?? 0,
                    rsvpAvatars: event.rsvpAvatars ?? [],
                    pinned: event.pinned ?? false,
                    venue_id: event.venue_id,
                    status: event.status,
                  }}
                  groupId={event.group_id}
                />
              ))
            )}
          </div>

          {mapQuery && (
            <div className={styles.mapSection}>
              <h2 className={styles.galleryTitle}>Map</h2>
              <VenueMapEmbed
                title={`${venue.venue_name} location`}
                query={mapQuery}
                className={styles.mapEmbed}
              />
            </div>
          )}

          <div className={styles.gallerySection}>
            <h2 className={styles.galleryTitle}>Gallery</h2>
            {galleryImages && galleryImages.length > 0 ? (
              <div className={styles.gallery}>
                {galleryImages.map((img: any) => (
                  <div key={img.id} className={styles.galleryItem}>
                    <img
                      onLoad={(e) => e.currentTarget.classList.add('loaded')}
                      src={
                        supabase.storage.from('venues').getPublicUrl(img.storage_path)
                          .data.publicUrl
                      }
                      alt="Venue Gallery"
                      className={styles.galleryImage}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyGallery}>
                <span className={styles.emptyIcon}>🖼️</span>
                <p className={styles.emptyText}>No photos yet</p>
              </div>
            )}
          </div>

          <div className={styles.tagsSection}>
            <div className={styles.tagsHeader}>
              <h2 className={styles.tagsTitle}>Interests</h2>
              <button
                className={styles.tagsToggle}
                onClick={() => setShowTags(!showTags)}
                aria-expanded={showTags}
              >
                {showTags ? 'Hide' : 'View'}
                <span
                  className={`${styles.tagsChevron} ${showTags ? styles.tagsChevronOpen : ''}`}
                >
                  ▼
                </span>
              </button>
            </div>
            <div className={`${styles.tagsBody} ${showTags ? styles.tagsBodyOpen : ''}`}>
              {tags.length > 0 ? (
                <div className={styles.tagsContainer}>
                  {tags.map((tag: string, index: number) => (
                    <span key={index} className={styles.tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyTags}>
                  <p className={styles.emptyTagsText}>No interests yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VenueProfile;
